import { forwardRef, useImperativeHandle, useRef } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useLoaderData } from 'react-router';
import { useCheckoutContext } from '../../context/CheckoutContextCoCo.js';
import { useSubscriptionConfigFormCoCo } from '../../hooks/useSubscriptionConfigFormCoCo.js';
import { ContactOrPurposeSectionCoCo } from '../shared/ContactOrPurposeSectionCoCo.js';
import { NumberPublicityCoCo } from '../shared/NumberPublicityCoCo';
import { NumberSelectionSectionCoCo } from '../shared/NumberSelectionSectionCoCo.js';
import { SimCardSectionCoCo } from '../shared/SimCardSectionCoCo';
import { hasConnectivityConfig } from '../useProductConfigSchemaCoCo.js';
import type { PlaceProductOrderLoaderData } from '../../../../common/loaders.js';
import type { PlaceOrderFormDataCoCo } from '../../placeOrderTypesCoCo.js';
import type { ConfigHandle, ProductConfigProps } from '../productConfigTypesCoCo.js';
import type { DirectoryListingPublicityPatch, NumberPrivacyHandle } from '../shared/NumberPublicityCoCo';

export const MobileOfferingConfigCoCo = forwardRef<ConfigHandle, ProductConfigProps>(
  (
    {
      basketItem,
      orderItem,
      schema,
      phoneNumbers,
      phoneNumbersLoading,
      onLoadMoreNumbers,
      disabledPhoneNumbers,
      initialPhoneNumber,
      contactFormRefs,
      contactsUpdateMethods,
      onPlanProductOfferingSpecRuntimeCharacteristicChange,
    },
    ref
  ) => {
    const { mdmId, customerInfo, selectedProductOffers } = useCheckoutContext();
    const { holidays } = useLoaderData() as PlaceProductOrderLoaderData;
    const outerForm = useFormContext<PlaceOrderFormDataCoCo>();
    const numberPrivacyRef = useRef<NumberPrivacyHandle>(null);

    const cartItemInstanceId = `${basketItem.id}-${orderItem.basketItemIndex}`;

    const { innerFormMethods, formState, errors, validatedPhoneNumbers, validateAllFields, scrollToFirstError } =
      useSubscriptionConfigFormCoCo({
        cartItemInstanceId,
        basketItem,
        mdmId,
        phoneNumbers,
        selectedProductOffers,
      });

    const handleDirectoryListingPublicityPatch = (patch: DirectoryListingPublicityPatch) => {
      const units = outerForm.getValues('products.placeOrderItemUnits') ?? [];
      const idx = units.findIndex(
        u => u.basketItemId === orderItem.basketItemId && u.basketItemIndex === orderItem.basketItemIndex
      );
      if (idx === -1) {
        return;
      }
      const nextUnits = [...units];
      nextUnits[idx] = { ...nextUnits[idx], ...patch };
      outerForm.setValue('products.placeOrderItemUnits', nextUnits, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        validate: async () => {
          const subscriptionValid = await validateAllFields();
          const numberPrivacyValid = (await numberPrivacyRef.current?.validate()) ?? true;
          return subscriptionValid && numberPrivacyValid;
        },
        scrollToFirstError: () => {
          if (Object.keys(errors).length > 0) {
            scrollToFirstError();
            return;
          }
          numberPrivacyRef.current?.scrollToFirstError();
        },
        getErrorCount: () => Object.keys(errors).length + (numberPrivacyRef.current?.getErrorCount() ?? 0),
      }),
      [validateAllFields, scrollToFirstError, errors]
    );

    const showConnectivityConfig = hasConnectivityConfig(schema);

    if (!schema.needsContact && !schema.needsCostCenter && !schema.needsReference && !showConnectivityConfig) {
      return null;
    }

    return (
      <div id={`subscription-config-${cartItemInstanceId}`}>
        <ContactOrPurposeSectionCoCo
          schema={schema}
          orderItem={orderItem}
          contactFormRefs={contactFormRefs}
          contactsUpdateMethods={contactsUpdateMethods}
          isMandatory={true}
          showAccordion={true}
        />

        <NumberPublicityCoCo
          ref={numberPrivacyRef}
          schema={schema}
          basketItemId={basketItem.id}
          cartItemId={orderItem.basketItemId}
          cartItemInstanceId={cartItemInstanceId}
          companyName={customerInfo.name ?? ''}
          onNumberPublicityChange={onPlanProductOfferingSpecRuntimeCharacteristicChange}
          initialDirectoryListingPublicity={orderItem.directoryListingPublicity}
          initialDirectoryListingPublicityDetails={orderItem.directoryListingCustomPublicityDetails}
          onDirectoryListingPublicityPatch={handleDirectoryListingPublicityPatch}
        />

        {showConnectivityConfig && (
          <div className="of-order-item-unit-coco__config-box of-order-item-unit-coco__config-box--borderless">
            <FormProvider {...innerFormMethods}>
              <NumberSelectionSectionCoCo
                schema={schema}
                cartItemInstanceId={cartItemInstanceId}
                companyName={customerInfo.name ?? ''}
                formState={formState}
                phoneNumbers={phoneNumbers}
                phoneNumbersLoading={phoneNumbersLoading}
                onLoadMoreNumbers={onLoadMoreNumbers}
                disabledPhoneNumbers={disabledPhoneNumbers}
                initialPhoneNumber={initialPhoneNumber}
                validatedPhoneNumbers={validatedPhoneNumbers}
                holidays={holidays}
              />
              <SimCardSectionCoCo
                schema={schema}
                basketItem={basketItem}
                cartItemInstanceId={cartItemInstanceId}
                formState={formState}
                errors={errors}
              />
            </FormProvider>
          </div>
        )}
      </div>
    );
  }
);

MobileOfferingConfigCoCo.displayName = 'MobileOfferingConfigCoCo';
